import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BrandRoot } from "@/components/BrandRoot";
import { OfferRequestForm } from "@/components/form/OfferRequestForm";
import { type ExtractionResult, PasteStep } from "@/components/form/PasteStep";
import { type BrandView, fetchBrand } from "@/lib/brandApi";
import { copy } from "@/lib/copy";

/**
 * La version que l'école colle dans une iframe sur son propre site.
 *
 * Pas d'en-tête, pas de fond, pas d'ombre, pas de lien de navigation : la page hôte
 * porte déjà l'identité de l'école, et un deuxième bandeau ferait doublon. Le
 * formulaire hérite quand même des tokens de l'école, pour que les champs et le
 * bouton s'accordent au site qui les accueille.
 *
 * La hauteur est renvoyée à la page hôte par `postMessage` : sans ça, l'iframe est
 * soit tronquée, soit trop haute, et personne ne peut le corriger de l'extérieur.
 */
export function EmbedPage() {
	const { slug = "" } = useParams();
	const [brand, setBrand] = useState<BrandView | null>(null);
	const [loaded, setLoaded] = useState(false);
	const [sent, setSent] = useState(false);
	// L'embed est l'autre surface de dépôt réelle : elle mérite la même entrée par
	// collage. La laisser au formulaire seul aurait fait deux produits différents
	// selon qu'on arrive par la page de l'école ou par son site.
	const [step, setStep] = useState<"paste" | "form">("form");
	const [extraction, setExtraction] = useState<ExtractionResult | null>(null);

	// ⚠️ Sans `.catch()`, une coupure ou un délai dépassé laissait l'iframe bloquée pour
	// toujours sur « Chargement… », sur le site de l'école : elle n'a aucun moyen de
	// voir la console de ses visiteurs, et le seul signe aurait été un cadre vide.
	const [echec, setEchec] = useState(false);

	useEffect(() => {
		fetchBrand(slug)
			.then((result) => {
				setBrand(result);
				setStep(result?.extractionAvailable ? "paste" : "form");
				setLoaded(true);
			})
			.catch(() => {
				setEchec(true);
				setLoaded(true);
			});
	}, [slug]);

	useEffect(() => {
		const publish = () =>
			window.parent?.postMessage(
				{ type: "mg-partner-embed:height", height: document.body.scrollHeight },
				"*",
			);
		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(document.body);
		return () => observer.disconnect();
	});

	if (!loaded) return <div className="p-6 text-mg-secondary">{copy.deposit.loading}</div>;

	// Libellé rendu ici, tel qu'il est écrit dans `src/lib/copy.ts` :
	//   loadFailedEmbed : « Formulaire momentanément indisponible. Rechargez la page. »
	if (echec) {
		return (
			<div role="alert" className="p-6 text-mg-heading">
				{copy.deposit.loadFailedEmbed}
			</div>
		);
	}

	// Sans cet état, un slug erroné laissait l'iframe bloquée pour toujours sur
	// « Chargement… », et l'école qui a collé le code d'intégration n'apprenait rien.
	if (!brand) {
		return (
			<div role="alert" className="p-6 text-mg-heading">
				{copy.deposit.unknownEmbed}
			</div>
		);
	}

	return (
		<BrandRoot brand={brand} className="bg-mg-surface">
			{sent ? (
				<div className="px-6 py-12 text-center">
					<span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-mg-hover">
						<svg viewBox="0 0 24 24" className="h-7 w-7 text-mg-action" fill="none" aria-hidden>
							<title>Envoyé</title>
							<path
								d="M4 12.5 9.5 18 20 6.5"
								stroke="currentColor"
								strokeWidth="2.2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</span>
					<p className="mt-5 t-h2 text-mg-heading">{copy.deposit.sentTitle}</p>
					<p className="t-body mx-auto mt-2 max-w-sm text-mg-secondary">
						{brand.confirmationMessage ?? copy.deposit.sentFallback(brand.schoolName)}
					</p>
				</div>
			) : step === "paste" ? (
				<div className="flex flex-col gap-5 px-1 py-2 sm:px-2">
					<p className="t-h3 text-mg-heading">{copy.paste.title}</p>
					<PasteStep
						brand={brand}
						onExtracted={(result) => {
							setExtraction(result);
							setStep("form");
						}}
						onSkip={() => setStep("form")}
					/>
				</div>
			) : (
				<div className="px-1 py-2 sm:px-2">
					<OfferRequestForm
						brand={brand}
						onSubmitted={() => setSent(true)}
						compact
						initialValues={
							extraction
								? {
										...extraction.fields,
										...(extraction.companyName ? { companyName: extraction.companyName } : {}),
									}
								: undefined
						}
					/>
				</div>
			)}
		</BrandRoot>
	);
}
